/**
 * Every keyboard shortcut the Werkbank answers, in one place (Welle 1, idea
 * 16): the overview behind „?" lists them per screen, and the tooltips and
 * menus that name a key read it from here — a key that changes in one
 * changes in the other.
 *
 * Only what the code answers. Each entry was read off its handler, and
 * `shortcuts.test.ts` names that handler and fails the day it stops matching:
 * nothing in here is a promise of a key somebody meant to wire.
 */

/** A key as `KeyboardEvent.key` names it, or `"Mod"` — Strg, ⌘ on a Mac. */
type Key = string;
/** Keys that do the same thing in one place — the arrows — drawn side by side. */
type KeySet = readonly Key[];
/** One way to press a shortcut: held together, modifiers first. */
export type Chord = readonly (Key | KeySet)[];

export interface Shortcut {
  /** The ways to press it; any one of them does it. */
  readonly chords: readonly Chord[];
  /** What it does — a short German sentence without a full stop. */
  readonly does: string;
}

const ARROWS: KeySet = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];

export const SHORTCUTS = {
  // ── Everywhere ──────────────────────────────────────────────────────────
  help: { chords: [["?"]], does: "Diese Übersicht öffnen und schließen" },
  close: { chords: [["Escape"]], does: "Offenen Dialog oder offenes Menü schließen" },

  // ── Vorgänge ────────────────────────────────────────────────────────────
  registerSearch: { chords: [["/"]], does: "In die Suche springen" },
  searchClear: { chords: [["Escape"]], does: "Suchbegriff löschen" },

  // ── Ein Vorgang ─────────────────────────────────────────────────────────
  noteFile: { chords: [["Mod", "Enter"]], does: "Notiz ablegen" },
  noteCancel: { chords: [["Escape"]], does: "Notiz abbrechen" },

  // ── A drawing surface that fills the window (/umriss, /planung) ─────────
  maximize: { chords: [["f"]], does: "Zeichenfläche maximieren und zurück" },
  maximizeExit: { chords: [["Escape"]], does: "Maximierte Zeichenfläche verlassen" },
  // The twins of „Rückgängig" and „Wiederholen" on both boards (Welle 1,
  // idea 7) — never more than the buttons, and not inside a field.
  undo: { chords: [["Mod", "z"]], does: "Rückgängig: den letzten Schritt zurücknehmen" },
  redo: { chords: [["Mod", "Shift", "z"], ["Control", "y"]], does: "Wiederholen: den zurückgenommenen Schritt wieder setzen" },

  // ── Umriss ──────────────────────────────────────────────────────────────
  outlineWallPush: { chords: [[ARROWS]], does: "Wand quer zu ihrer Richtung um 10 mm verschieben" },
  outlineWallRemove: {
    chords: [["Delete"], ["Backspace"]],
    does: "Wand entfernen — ein geschlossener Umriss öffnet sich dort",
  },
  outlineCornerRemove: {
    chords: [["Delete"], ["Backspace"]],
    does: "Ecke entfernen, wo ihre zwei Wände auf einer Linie liegen",
  },

  // ── Prüfung (read off the review screen, which is not changed here) ─────
  reviewTakeOver: { chords: [["Mod", "Enter"]], does: "Übernehmen und planen" },
  reviewUndo: { chords: [["Mod", "z"]], does: "Zuletzt gesetzte Zahl zurücknehmen, außerhalb eines Feldes" },
  reviewMaximize: { chords: [["f"]], does: "Vorschlag oder Original groß zeigen und zurück" },
  reviewMaximizeExit: { chords: [["Escape"]], does: "Große Ansicht verlassen" },
  reviewSet: { chords: [["Enter"]], does: "Zahl setzen und zur nächsten Wand springen" },
  reviewFieldReset: { chords: [["Escape"]], does: "Eingabe verwerfen" },

  // ── Planung ─────────────────────────────────────────────────────────────
  planningEscape: {
    chords: [["Escape"]],
    does: "Eins zurück: Ziehen abbrechen, Menü schließen, Werkzeug weglegen, maximierte Ansicht verlassen, Auswahl aufheben",
  },
  planningTools: { chords: [[["ArrowLeft", "ArrowRight"]]], does: "Werkzeug wechseln" },
  zoomIn: { chords: [["+"], ["Mod", "+"]], does: "Vergrößern" },
  zoomOut: { chords: [["-"], ["Mod", "-"]], does: "Verkleinern" },
  zoomFit: { chords: [["0"], ["Mod", "0"]], does: "Alles zeigen" },
  pan: { chords: [[ARROWS]], does: "Ansicht verschieben, solange sie vergrößert ist" },
  panDrag: { chords: [[" "]], does: "Gedrückt halten und mit der Maus ziehen: Ansicht verschieben" },
  nudge: { chords: [[ARROWS]], does: "Rost oder Objekt um 10 mm entlang der Wand verschieben" },
  cutoutNudge: {
    chords: [[["ArrowLeft", "ArrowRight"]], [["ArrowUp", "ArrowDown"]]],
    does: "Ausschnitt um 10 mm verschieben, entlang der Wand oder quer",
  },
  nudgeCoarse: { chords: [["Shift", ARROWS]], does: "Dasselbe in Schritten von 100 mm" },
  toEnds: { chords: [["Home"], ["End"]], does: "Rost oder Objekt an den Anfang oder das Ende der Wand" },
  remove: { chords: [["Delete"], ["Backspace"]], does: "Rost, Objekt oder Ausschnitt entfernen" },
  wallEnter: { chords: [["Enter"], [" "]], does: "Wand wählen — mit einem Werkzeug in der Hand darauf einsetzen" },
  contextMenu: { chords: [["Shift", "F10"]], does: "Menü der Wand oder der Konsole öffnen" },
  cornerMiter: { chords: [["Enter"], [" "]], does: "Am Eckgriff: Gehrung setzen oder entfernen" },
  measureSet: { chords: [["Enter"]], does: "Maß setzen" },
  measureReset: { chords: [["Escape"]], does: "Auf das gespeicherte Maß zurücksetzen" },

  // ── Kalkulation ─────────────────────────────────────────────────────────
  marginCommit: { chords: [["Enter"]], does: "Neue Marge übernehmen und neu rechnen" },
} as const satisfies Record<string, Shortcut>;

export type ShortcutId = keyof typeof SHORTCUTS;

/** A few shortcuts that act in one place — a field, the focused element. */
export interface ShortcutGroup {
  readonly title: string;
  /** One sentence that holds for the whole group. */
  readonly note?: string;
  readonly ids: readonly ShortcutId[];
}

export interface ScreenHelp {
  /** The screen as the navigation and its head name it. */
  readonly name: string;
  readonly groups: readonly ShortcutGroup[];
}

/** Under every screen's own keys. */
export const EVERYWHERE: ShortcutGroup = { title: "Überall", ids: ["help", "close"] };

/** Where the boards' undo keys stand back: a field keeps its own. */
const UNDO_NOTE = "In einem Eingabefeld nehmen dieselben Tasten die letzten Zeichen zurück, nicht den Schritt.";

/** The screens with keys of their own — and the start page, which has none. */
export type ScreenKey = "start" | "register" | "record" | "outline" | "review" | "planning" | "calculation";

export const SCREENS: Readonly<Record<ScreenKey, ScreenHelp>> = {
  start: { name: "Start", groups: [] },
  register: {
    name: "Vorgänge",
    groups: [
      { title: "Liste", ids: ["registerSearch"] },
      { title: "In der Suche", ids: ["searchClear"] },
    ],
  },
  record: {
    name: "Vorgang",
    groups: [{ title: "Im Notizfeld", ids: ["noteFile", "noteCancel"] }],
  },
  outline: {
    name: "Umriss",
    groups: [
      { title: "Zeichenfläche", ids: ["maximize", "maximizeExit"] },
      { title: "Schritte", note: UNDO_NOTE, ids: ["undo", "redo"] },
      { title: "Wand im Fokus", ids: ["outlineWallPush", "outlineWallRemove"] },
      { title: "Ecke im Fokus", ids: ["outlineCornerRemove"] },
    ],
  },
  review: {
    name: "Prüfung",
    groups: [
      { title: "Auf dem ganzen Schirm", ids: ["reviewTakeOver", "reviewUndo", "reviewMaximize", "reviewMaximizeExit"] },
      { title: "Im Maßfeld einer Wand", ids: ["reviewSet", "reviewFieldReset"] },
    ],
  },
  planning: {
    name: "Planung",
    groups: [
      { title: "Allgemein", ids: ["maximize", "planningEscape"] },
      { title: "Schritte", note: UNDO_NOTE, ids: ["undo", "redo"] },
      { title: "Werkzeugleiste", ids: ["planningTools"] },
      {
        title: "Ansicht, Brett im Fokus",
        note: "Mit Strg oder ⌘ wirken Plus, Minus und 0 bei maximierter Zeichenfläche auch ohne Fokus auf dem Brett.",
        ids: ["zoomIn", "zoomOut", "zoomFit", "pan", "panDrag"],
      },
      {
        title: "Rost, Objekt oder Ausschnitt im Fokus",
        note: "Bei vergrößerter Ansicht verschieben die Pfeiltasten die Ansicht, nicht das Teil.",
        ids: ["nudge", "cutoutNudge", "nudgeCoarse", "toEnds", "remove"],
      },
      { title: "Wand, Ecke oder Konsole im Fokus", ids: ["wallEnter", "cornerMiter", "contextMenu"] },
      { title: "Im Feld „Maß“", ids: ["measureSet", "measureReset"] },
    ],
  },
  calculation: {
    name: "Kalkulation",
    groups: [{ title: "Im Feld „Marge %“", ids: ["marginCommit"] }],
  },
};

/**
 * Which screen an address shows, as far as its keys go — `null` where none
 * of its own are listed.
 *
 * `/vorgang/…/schema` holds more than the review: the form choice, the old
 * schema view, a reading still in work. Its keys answer only while the
 * review itself is on screen, so the caller says whether it is
 * (`reviewOnScreen`) — read off the page, because the review screen is not
 * changed while the M2 measurements run. Where it is on screen, it is the
 * review, at whatever address (the dev preview has its own).
 */
export function screenAt(pathname: string, reviewOnScreen: boolean): ScreenKey | null {
  if (reviewOnScreen) return "review";
  if (pathname === "/") return "start";
  if (pathname === "/vorgaenge") return "register";
  if (pathname === "/umriss") return "outline";
  if (pathname === "/planung") return "planning";
  const step = /^\/vorgang\/[^/]+(?:\/([^/]+))?\/?$/.exec(pathname);
  if (step === null) return null;
  switch (step[1]) {
    case undefined:
      return "record";
    case "planung":
      return "planning";
    case "kalkulation":
      return "calculation";
    default:
      return null;
  }
}

/** ⌘ on a Mac, Strg everywhere else — K60 works on Windows. */
export function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

/** The label on the key cap, as a German keyboard prints it. */
const CAPS: Record<string, string> = {
  Escape: "Esc",
  Delete: "Entf",
  Backspace: "Rücktaste",
  Home: "Pos1",
  End: "Ende",
  " ": "Leertaste",
  ArrowLeft: "←",
  ArrowRight: "→",
  ArrowUp: "↑",
  ArrowDown: "↓",
  "-": "−",
};

/** One key as a `<Kbd>` shows it. */
export function keyCap(key: Key, mac: boolean): string {
  if (key === "Mod") return mac ? "⌘" : "Strg";
  // The Control key itself, also on a Mac — where Strg+Y is ⌃Y, not ⌘Y.
  if (key === "Control") return mac ? "⌃" : "Strg";
  return CAPS[key] ?? (key.length === 1 ? key.toUpperCase() : key);
}

/** A key as a tooltip says it in brackets — the sign keys by name. */
const WORDS: Record<string, string> = { Mod: "Strg/⌘", Control: "Strg", "+": "Plus", "-": "Minus" };

function keyWord(key: Key): string {
  return WORDS[key] ?? keyCap(key, false);
}

/**
 * A shortcut in the words a tooltip uses — „F", „Strg/⌘ + Minus", „Entf
 * oder Rücktaste". Every way to press it, or only the one at `chord`.
 */
export function shortcutWords(id: ShortcutId, chord?: number): string {
  const chords: readonly Chord[] = SHORTCUTS[id].chords;
  const picked = chord === undefined ? chords : [chords[chord]!];
  return picked
    .map((each) => each.map((part) => (typeof part === "string" ? keyWord(part) : part.map(keyWord).join(" "))).join(" + "))
    .join(" oder ");
}

/**
 * The caps of a shortcut's first way to press it — what a menu shows beside
 * an item whose action a key also does („Entf" beside „Element entfernen").
 */
export function firstChordCaps(id: ShortcutId, mac: boolean): string[] {
  const chord: Chord = SHORTCUTS[id].chords[0];
  return chord.flatMap((part) => (typeof part === "string" ? [part] : [...part])).map((key) => keyCap(key, mac));
}

/** Input types that take text — where „?" or „/" is a character, not a shortcut. */
const NON_TEXT_INPUTS = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);

/** Whether a keystroke was meant for something a person is typing into. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(target.type);
  return target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}
